/* app/bi-master.jsx - the shared BI master template.
   Competitors / Industry / Partners are wired to the live /api/bi/* backend
   (list/detail/status/ours/delete), scoped to the active site. Products keeps
   its own (mock) path until its sub-step. Markup is the original design. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  // Types wired to the backend (Products uses bi-products' card/detail components).
  const WIRED = new Set(["competitor", "industry", "partner", "product"]);
  // prototype facet display field → backend data key
  const FACET_KEY = { pstatus: "status", pipeline: "status" };
  const REL_MAP = { lookalike: "look", complementary: "comp", alternative: "alt" };
  const STATUS_MAP = { validated: "validated", pending: "pending", rejected: "archived" };
  const SRC_MAP = { manual: "manual", fetched: "fetched", ai_suggested: "ai", feed: "feed" };
  // Entity URLs are stored/displayed without a scheme ("acme.com"), so a bare
  // href would resolve relative to the app. Re-attach https:// for the link and
  // keep showing the bare host. Returns null when there's nothing to link to,
  // so callers can render plain text instead of a dead link.
  function hrefOf(url) {
    const u = String(url || "").trim();
    if (!u) return null;
    return /^https?:\/\//i.test(u) ? u : "https://" + u;
  }
  // Anchor props shared by every outbound link: new tab, no referrer leakage,
  // and no row-level click handler firing underneath.
  const outbound = (url) => ({
    href: hrefOf(url), target: "_blank", rel: "noopener noreferrer",
    onClick: (ev) => ev.stopPropagation(),
  });

  // backend data.* key → display label, per entity type (drives card/detail meta)
  const FIELDS = {
    competitor: [["Market", "market"], ["Positioning", "positioning"], ["Pricing", "pricing"]],
    industry: [["Service line", "service_line"], ["Geography", "geography"], ["Notes", "notes"]],
    partner: [["Market", "market"], ["Type", "partner_type"], ["Region", "region"], ["Services", "services"]],
  };
  function entityFromRow(row, cfg) {
    const d = row.data || {};
    const rels = (Array.isArray(d.relationships_to_me) ? d.relationships_to_me : []).map(r => REL_MAP[r]).filter(Boolean);
    const verified = row.last_verified_at ? String(row.last_verified_at).slice(0, 10)
      : row.updated_at ? String(row.updated_at).slice(0, 10) : "-";
    const e = {
      id: row.id,
      name: row.name || "",
      url: String(row.url || "").replace(/^https?:\/\//, "").replace(/\/+$/, ""),
      rels,
      status: STATUS_MAP[row.validation_status] || "validated",
      source: SRC_MAP[row.source] || "manual",
      ours: !!row.is_ours,
      verified,
      meta: (FIELDS[cfg.noun] || []).map(([label, key]) => [label, d[key] || "-"]),
      _data: d,
    };
    if (cfg.noun === "partner") { e.engagement = d.engagement || "indirect"; e.pstatus = d.status || "potential"; e.type = d.partner_type || ""; e.tag = d.partner_type || ""; }
    if (cfg.noun === "industry") { e.serviceLine = d.service_line || ""; e.tag = "Segment"; }
    if (cfg.noun === "product") {
      const provs = (Array.isArray(d.providers) ? d.providers : []).map(p => ({
        kind: p.kind === "competitor" ? "comp" : p.kind, name: p.name || "", price: p.pricing || "",
        seen: p.last_seen || "", tracked: !!p.ref_entity_id, url: p.url || "",
      }));
      const comp = provs.filter(p => p.kind === "comp").length;
      const nonUs = provs.filter(p => p.kind !== "us").length;
      e.serviceLine = d.category || "-";
      e.serviceLineId = d.category || "";
      e.market = d.market || "-";
      e.positioning = d.positioning || "";
      e.pipeline = d.status || (row.is_ours ? "live" : "consideration");
      e.providers = provs;
      e.coverage = d.possible_duplicate ? "dup" : (!row.is_ours && nonUs ? "gap" : comp >= 2 ? "contested" : "offer");
      e.coverage2 = row.is_ours ? "offer" : (comp >= 2 ? "contested" : "gap");
    }
    return e;
  }

  const REL = { look: ["Lookalike", "look"], comp: ["Complementary", "comp"], alt: ["Alternative", "alt"] };
  const relBadge = (r) => <span key={r} className={"bi-badge bi-badge--" + REL[r][1]}>{REL[r][0]}</span>;
  const srcBadge = (s) => {
    const map = { manual: ["edit", "Manual", "manual"], fetched: ["globe", "Fetched", "fetched"], ai: ["sparkles", "AI suggested", "ai"], feed: ["refresh", "Feed", "feed"] };
    const [icon, label, cls] = map[s];
    return <span className={"bi-badge bi-badge--" + cls}><I name={icon} size={11} />{label}</span>;
  };
  const valBadge = (status) => {
    if (status === "pending") return <span className="bi-badge bi-badge--pending"><I name="clock" size={11} />Pending review</span>;
    if (status === "archived") return <span className="bi-badge bi-badge--archived"><I name="archive" size={11} />Archived</span>;
    return null;
  };

  // ---- Competitors baseline config + data ----
  const COMPETITORS = {
    title: "Competitors", icon: "flag", noun: "competitor", hasUrl: true, ownable: true, shareable: true,
    // Fields the URL extractor fills in. Mirrors the module's own detail rows,
    // and tells /bi/extract-from-url what to look for: without a list it falls
    // back to competitor-worded prompts whatever the module is.
    fields: [{ key: "market", label: "Market" }, { key: "positioning", label: "Positioning" }, { key: "pricing", label: "Pricing" }],
    filters: [{ label: "Relationship", field: "rels", array: true, opts: [["look", "Lookalike", "look"], ["comp", "Complementary", "comp"], ["alt", "Alternative", "alt"]] }],  };

  const INDUSTRY = {
    title: "Industry", icon: "globe", noun: "industry", hasUrl: false, ownable: true, oursLabel: "We operate in", nestable: true, shareable: false,
    fields: [{ key: "service_line", label: "Service line" }, { key: "geography", label: "Geography" }, { key: "notes", label: "Notes" }],
    filters: [{ label: "Service line", field: "serviceLine", opts: [["formation", "Company formation", null], ["tax", "Tax & VAT", null], ["compliance", "Compliance", null], ["banking", "Banking", null], ["advisory", "Advisory", null]] }],  };

  const PARTNERS = {
    title: "Partners", icon: "git-branch", noun: "partner", hasUrl: true, ownable: false, shareable: true, nestable: false,
    fields: [{ key: "market", label: "Market" }, { key: "partner_type", label: "Type" }, { key: "region", label: "Region" }, { key: "services", label: "Services" }],
    facets: [{ field: "engagement", label: "Engagement", opts: [["direct", "Direct"], ["indirect", "Indirect"]] }, { field: "pstatus", label: "Status", opts: [["potential", "Potential"], ["prospect", "Prospect"], ["active", "Active"], ["declined", "Declined"]] }],
    filters: [
      { label: "Engagement", field: "engagement", opts: [["direct", "Direct", "direct"], ["indirect", "Indirect", "indirect"]] },
      { label: "Status", field: "pstatus", opts: [["potential", "Potential", "potential"], ["prospect", "Prospect", "prospect"], ["active", "Active", "active"], ["declined", "Declined", "declined"]] },
      { label: "Type", field: "type", opts: [["gov", "Government agency", null], ["private", "Private company", null], ["bank", "Bank / financial", null]] },
    ],  };

  const LIFECYCLE = [
    ["tracked", "Tracked", "var(--primary)", e => e.status !== "archived"],
    ["validated", "Validated", "var(--success)", e => e.status === "validated"],
    ["pending", "Pending review", "var(--warning)", e => e.status === "pending"],
    ["archived", "Archived", "var(--text-muted)", e => e.status === "archived"],
  ];

  function StatCards({ cfg, active, onPick, data, wired }) {
    // Counts come from the real rows once wired; only the un-wired prototype
    // preview falls back to the sample cfg.entities.
    const src = wired ? (data || []) : (cfg.entities || []);
    const lastVerified = wired
      ? (src.map(e => e.verified).filter(v => v && v !== "-").sort().slice(-1)[0] || "-")
      : "5 hours ago";
    return (
      <div className="bi-stat-cards">
        {LIFECYCLE.map(([id, label, color, fn]) => {
          const n = src.filter(fn).length;
          return (
            <button key={id} className={"bi-stat-card bi-stat-card--clickable" + (active === id ? " bi-stat-card--active" : "")} onClick={() => onPick(active === id ? "tracked" : id)}>
              <div className="bi-stat-card__top"><span className="bi-stat-card__dot" style={{ background: color }} /><span className="bi-stat-card__label">{label}</span></div>
              <span className="bi-stat-card__value">{n}</span>
            </button>
          );
        })}
        <div className="bi-stat-card"><div className="bi-stat-card__top"><span className="bi-stat-card__label">Last verified</span></div><span className="bi-stat-card__value bi-stat-card__value--text">{lastVerified}</span></div>
      </div>
    );
  }

  const fmatch = (f, e, id) => f.array ? (e[f.field] || []).includes(id) : e[f.field] === id;
  function FilterPill({ f, active, count, onSet }) {
    const [open, setOpen] = useState(false);
    const cur = active && active !== "all" ? f.opts.find(o => o[0] === active) : null;
    return (
      <div className={"bi-filterpill" + (cur ? " bi-filterpill--active" : "")}>
        <button className="bi-filterpill__btn" onClick={() => setOpen(o => !o)}>
          {cur && cur[2] && <span className={"bi-filterpill__dot bi-cat-" + cur[2]} />}
          {f.label}{cur && <>:&nbsp;<span className="bi-filterpill__val">{cur[1]}</span></>}
          {cur ? <span className="bi-filterpill__x" onClick={e => { e.stopPropagation(); onSet("all"); }}><I name="x" size={13} /></span> : <I name="chevron-down" size={14} />}
        </button>
        {open && <>
          <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="bi-filtermenu">
            <button className="bi-filtermenu__item" onClick={() => { onSet("all"); setOpen(false); }}>All<span className="bi-filtermenu__n">{count("all")}</span>{(!active || active === "all") && <span className="bi-filtermenu__check"><I name="check" size={15} /></span>}</button>
            {f.opts.map(([id, label, cat]) => (
              <button key={id} className="bi-filtermenu__item" onClick={() => { onSet(id); setOpen(false); }}>
                {cat && <span className={"bi-filterpill__dot bi-cat-" + cat} />}{label}<span className="bi-filtermenu__n">{count(id)}</span>{active === id && <span className="bi-filtermenu__check"><I name="check" size={15} /></span>}
              </button>
            ))}
          </div>
        </>}
      </div>
    );
  }
  function FilterBar({ cfg, active, setActive, data }) {
    const rows = data || cfg.entities || [];
    const countFor = (f, id) => rows.filter(e => e.status !== "archived" && (id === "all" || fmatch(f, e, id))).length;
    const anyActive = Object.values(active).some(v => v && v !== "all");
    return (
      <div className="bi-filterbar">
        <span className="bi-filterbar__label"><I name="filter" size={13} />Filter</span>
        {cfg.filters.map(f => <FilterPill key={f.field} f={f} active={active[f.field]} count={id => countFor(f, id)} onSet={v => setActive(f.field, v)} />)}
        {anyActive && <button className="bi-clearall" onClick={() => setActive("__clear__")}>Clear all</button>}
      </div>
    );
  }
  const FACET_COLOR = { direct: "var(--indigo-600)", indirect: "var(--gray-400)", potential: "var(--gray-400)", prospect: "var(--blue-500)", active: "var(--green-500)", declined: "var(--red-500)", Idea: "var(--gray-400)", Planned: "var(--blue-500)", Live: "var(--green-500)", Retired: "var(--gray-400)" };
  function FacetDropdown({ label, value, opts, colorOf, down, onChange }) {
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState(value);
    useEffect(() => { setVal(value); }, [value]);
    const cur = opts.find(o => o[0] === val) || opts[0];
    return (
      <div className="bi-facetdd" onClick={e => e.stopPropagation()}>
        <button className="bi-facetbtn" style={{ color: colorOf(val), borderColor: colorOf(val) }} onClick={() => setOpen(o => !o)}>
          <span className="bi-facetbtn__dot" style={{ background: colorOf(val) }} />{cur[1]}<I name="chevron-down" size={12} />
        </button>
        {open && <>
          <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className={"bi-facetmenu" + (down ? " bi-facetmenu--down" : "")}>
            {label && <div className="bi-facetmenu__label">{label}</div>}
            {opts.map(([id, l]) => (
              <button key={id} className="bi-facetmenu__item" onClick={() => { setVal(id); setOpen(false); if (id !== val) onChange && onChange(id); }}>
                <span className="bi-facetbtn__dot" style={{ background: colorOf(id) }} />{l}{val === id && <span className="bi-facetmenu__check"><I name="check" size={15} /></span>}
              </button>
            ))}
          </div>
        </>}
      </div>
    );
  }
  function Facets({ cfg, e, onFacet }) {
    if (!cfg.facets) return <span className="bi-foot-date">Last verified {e.verified}</span>;
    return cfg.facets.map(f => <FacetDropdown key={f.field} label={f.label || f.field} value={e[f.field]} opts={f.opts} down colorOf={v => FACET_COLOR[v] || "var(--text-secondary)"} onChange={onFacet ? (val) => onFacet(e.id, f.field, val) : undefined} />);
  }

  // "Move to…" re-homes a mis-classified entity into another BI module by
  // changing its entity_type via PATCH (e.g. a lead fetched as a competitor →
  // Partners). See docs/CHANGELOG "Move between modules".
  const ENTITY_TYPES = [["competitor", "Competitors"], ["industry", "Industry"], ["partner", "Partners"], ["product", "Products"]];
  function MoveMenu({ e, cfg, onMove }) {
    const [open, setOpen] = useState(false);
    return (
      <span style={{ position: "relative", display: "inline-flex" }} onClick={ev => ev.stopPropagation()}>
        <button className="bi-iconbtn" title="Move to another module" onClick={() => setOpen(o => !o)}><I name="shuffle" size={16} /></button>
        {open && <>
          <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="bi-filtermenu" style={{ right: 0, top: "100%" }}>
            <div style={{ padding: "7px 12px 3px", fontSize: "var(--text-xs)", color: "var(--text-muted)", fontWeight: 600 }}>Move to module</div>
            {ENTITY_TYPES.filter(([t]) => t !== cfg.noun).map(([t, label]) => (
              <button key={t} className="bi-filtermenu__item" onClick={() => { onMove && onMove(e.id, t); setOpen(false); }}>{label}</button>
            ))}
          </div>
        </>}
      </span>
    );
  }

  function EntityCard({ cfg, e, selectMode, selected, onToggle, onOurs, onOpen, onFacet, onShare, onMove }) {
    const cls = ["bi-card"];
    if (e.status === "pending") cls.push("bi-card--pending");
    if (e.status === "archived") cls.push("bi-card--archived");
    if (e.ours) cls.push("bi-card--ours");
    const card = (
      <div className={cls.join(" ")} tabIndex={0} onClick={() => onOpen && onOpen(e)}>
        <div className="bi-card-head">
          <div className="bi-card__name">{e.ours && <I name="star" size={15} className="bi-ours-star" style={{ fill: "currentColor" }} />}{e.name}</div>
          <div className="bi-card-badges">
            {valBadge(e.status)}
            {(e.rels || []).map(relBadge)}
            {e.tag && <span className="bi-badge bi-badge--manual">{e.tag}</span>}
            {srcBadge(e.source)}
          </div>
        </div>
        {cfg.hasUrl && (e.url
          ? <a className="bi-card__url" {...outbound(e.url)} title={"Open " + e.url}><I name="link" size={13} />{e.url}</a>
          : <span className="bi-card__url">No website</span>)}
        <div className="bi-card__meta">{(e.meta || []).map(([l, v]) => <div className="bi-meta-row" key={l}><span className="bi-meta-row__l">{l}</span><span className="bi-meta-row__v">{v}</span></div>)}</div>
        {cfg.nestable && e.children && <div className="bi-variants-row"><I name="chevron-right" size={13} className="bi-chev" />{e.children.length} sub-segments · {e.children.join(", ")}</div>}
        <div className="bi-card-foot">
          <div className="bi-card-foot__left"><Facets cfg={cfg} e={e} onFacet={onFacet} /></div>
          <div className="bi-card-foot__right">
            {cfg.ownable && <button className={"bi-iconbtn bi-ours-toggle" + (e.ours ? " is-ours" : "")} title={e.ours ? (cfg.oursLabel || "Marked as ours") : (cfg.oursLabel || "Mark as ours")} onClick={ev => { ev.stopPropagation(); onOurs(e.id); }}><I name="star" size={16} /></button>}
            {cfg.shareable && <button className="bi-iconbtn" title="Share to a site" onClick={ev => { ev.stopPropagation(); onShare && onShare(e); }}><I name="share" size={16} /></button>}
            <MoveMenu e={e} cfg={cfg} onMove={onMove} />
            <span className="bi-viewdetails">View details<I name="arrow-right" size={14} /></span>
          </div>
        </div>
      </div>
    );
    if (!selectMode) return card;
    return (
      <div className={"bi-select-wrap bi-select-wrap--on" + (selected ? " bi-select-wrap--selected" : "")}>
        <label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={selected} onChange={() => onToggle(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
        {card}
      </div>
    );
  }

  function EntityRow({ cfg, e, selectMode, selected, onToggle, onOurs, onOpen, onShare, onMove }) {
    const cls = ["bi-row"];
    if (e.status === "pending") cls.push("bi-row--pending");
    if (e.status === "archived") cls.push("bi-row--archived");
    if (e.ours) cls.push("bi-row--ours");
    const row = (
      <div className={cls.join(" ")} tabIndex={0} onClick={() => onOpen && onOpen(e)}>
        <div className="bi-row__main">
          <span className="bi-row__name">{e.ours && <I name="star" size={13} className="bi-ours-star" />}{e.name}</span>
          {cfg.hasUrl
            ? (e.url
                ? <a className="bi-row__url" {...outbound(e.url)} title={"Open " + e.url}><I name="link" size={12} />{e.url}</a>
                : <span className="bi-row__url">No website</span>)
            : <span className="bi-row__url">{e.meta[0] && e.meta[0][1]}</span>}
        </div>
        <div className="bi-row__badges">{valBadge(e.status)}{(e.rels || []).map(relBadge)}{e.tag && <span className="bi-badge bi-badge--manual">{e.tag}</span>}{srcBadge(e.source)}</div>
        <div className="bi-row__meta">{e.meta[1] ? e.meta[1][0] + ": " + e.meta[1][1] : ""}</div>
        <div className="bi-row__date">Verified {e.verified}</div>
        <div className="bi-row__actions">
          {cfg.ownable && <button className={"bi-iconbtn bi-ours-toggle" + (e.ours ? " is-ours" : "")} title={e.ours ? "Marked as ours" : "Mark as ours"} onClick={ev => { ev.stopPropagation(); onOurs(e.id); }}><I name="star" size={16} /></button>}
          {cfg.shareable && <button className="bi-iconbtn" title="Share to a site" onClick={ev => { ev.stopPropagation(); onShare && onShare(e); }}><I name="share" size={16} /></button>}
          <MoveMenu e={e} cfg={cfg} onMove={onMove} />
          <span className="bi-viewdetails">View<I name="arrow-right" size={14} /></span>
        </div>
      </div>
    );
    if (!selectMode) return row;
    return (
      <div className={"bi-select-wrap bi-select-wrap--on" + (selected ? " bi-select-wrap--selected" : "")}>
        <label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={selected} onChange={() => onToggle(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
        {row}
      </div>
    );
  }

  function DetailView({ cfg, e, onBack, onOurs, onStatus, onDelete, onEdit }) {
    const validated = e.status === "validated";
    const archived = e.status === "archived";
    const provLabel = { manual: "manual entry", fetched: "a web fetch", ai: "an AI suggestion", feed: "a data feed" }[e.source];
    const st = (s) => onStatus && onStatus(e.id, s);
    return (
      <div className="bi-detail">
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to {cfg.title}</button>
        <div className="bi-detail-head">
          <div>
            <div className="bi-detail__name">{e.ours && <I name="star" size={20} className="bi-ours-star" />}{e.name}</div>
            <div className="bi-detail__badges">{valBadge(e.status) || <span className="bi-badge bi-badge--validated"><I name="check" size={11} />Validated</span>}{e.rels.map(relBadge)}{srcBadge(e.source)}</div>
          </div>
          <div className="bi-detail-actions">
            {validated
              ? <button className="bi-btn" onClick={() => st("pending")}><I name="clock" size={16} />Move to pending</button>
              : <button className="bi-btn bi-btn--primary" onClick={() => st("validated")}><I name="check-circle" size={16} />Validate</button>}
            {archived
              ? <button className="bi-btn" onClick={() => st("pending")}><I name="rotate-ccw" size={16} />Restore</button>
              : <button className="bi-btn" onClick={() => st("rejected")}><I name="archive" size={16} />Archive</button>}
            <button className="bi-btn" onClick={() => onEdit && onEdit(e)}><I name="edit" size={16} />Edit</button>
            <button className="bi-btn bi-btn--danger" onClick={() => onDelete && onDelete(e.id)}><I name="trash" size={16} />Delete</button>
          </div>
        </div>

        <div className="bi-detail-section">
          <div className="bi-detail-section__h">Profile</div>
          <div className="bi-detail-section__body">
            {cfg.hasUrl && <div className="bi-detail-row"><span className="bi-detail-row__l">Website</span><span className="bi-detail-row__v">{e.url
              ? <a className="bi-card__url" {...outbound(e.url)} title={"Open " + e.url}><I name="link" size={13} />{e.url}<I name="external-link" size={12} /></a>
              : <span className="bi-card__url">No website on file</span>}</span></div>}
            {cfg.ownable && <div className="bi-detail-row"><span className="bi-detail-row__l">Ownership</span><span className="bi-detail-row__v"><button className={"bi-iconbtn bi-ours-toggle" + (e.ours ? " is-ours" : "")} onClick={() => onOurs(e.id)} style={{ width: "auto", padding: "0 8px", gap: 6, fontSize: "var(--text-xs)", fontWeight: 600 }}><I name="star" size={14} />{e.ours ? (cfg.oursLabel ? cfg.oursLabel : "Marked as ours") : (cfg.oursLabel ? cfg.oursLabel : "Mark as ours")}</button></span></div>}
            {e.meta.map(([l, v]) => <div className="bi-detail-row" key={l}><span className="bi-detail-row__l">{l}</span><span className="bi-detail-row__v">{v}</span></div>)}
            <div className="bi-detail-row"><span className="bi-detail-row__l">Relationship</span><span className="bi-detail-row__v">{e.rels.length ? e.rels.map(relBadge) : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>None</span>}</span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Discovered via</span><span className="bi-detail-row__v"><span className="bi-detail-prov">{srcBadge(e.source)} {provLabel}</span></span></div>
            <div className="bi-detail-row"><span className="bi-detail-row__l">Status &amp; verification</span><span className="bi-detail-row__v" style={{ fontWeight: 400, color: "var(--text-secondary)" }}>{validated ? "Validated" : archived ? "Archived" : "Pending review"} · last verified {e.verified}</span></div>
          </div>
        </div>

        {cfg.nestable && (
          <div className="bi-detail-section">
            <div className="bi-detail-section__h">Sub-{cfg.noun}s<button className="bi-btn bi-btn--sm"><I name="plus" size={14} />Add a sub-{cfg.noun}</button></div>
            <div className="bi-detail-section__body">
              <div className="bi-crumb"><I name="home" size={12} />{cfg.title} <I name="chevron-right" size={12} /> {e.name}</div>
              {(e.children || ["Digital banking", "Payments"]).map(c => <div className="bi-childrow" key={c}><I name="box" size={15} style={{ color: "var(--text-muted)" }} /><span className="bi-childrow__name">{c}</span><I name="chevron-right" size={16} className="bi-childrow__chev" /></div>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- the module ----
  // `detailId` / `onDetail` put the open entity in the URL (the location
  // convention lives in main.jsx). They replaced a one-shot `initialEntityId`
  // that could only be delivered by remounting the module, and could say
  // "open this" but never "close it", which is exactly what browser Back has
  // to be able to say.
  function BiModule({ cfg, marketSet, state, site, sites, go, onViewChange, detailId, onDetail }) {
    const wired = WIRED.has(cfg.noun);
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [lifecycle, setLifecycle] = useState("tracked");
    const [filters, setFilters] = useState({});
    const setFilter = (field, val) => field === "__clear__" ? setFilters({}) : setFilters(f => ({ ...f, [field]: val }));
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [data, setData] = useState(wired ? [] : (cfg.entities || []));
    const [phase, setPhase] = useState(wired ? "loading" : (state || "default"));
    const [market, setMarket] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    // Behind the same detail/setDetail pair every handler below already used,
    // so the switch to host-owned state changed nothing else in here. The local
    // state is the fallback for a host that does not own the location (the
    // component gallery mounts these modules bare).
    const [localDetail, setLocalDetail] = useState(null);
    const detail = onDetail ? (detailId == null ? null : { id: detailId }) : localDetail;
    const setDetail = onDetail ? ((d) => onDetail(d == null ? null : d.id)) : setLocalDetail;
    const [modal, setModal] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);   // { ids:[], one? } | null
    const [editing, setEditing] = useState(null);
    const [shareEntity, setShareEntity] = useState(null);
    const M = window.MlBiModals || {};
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    const reload = useCallback(async () => {
      if (!wired) return;
      if (siteId == null) { setData([]); setPhase("default"); return; }
      setPhase("loading");
      try {
        const rows = await api(`/bi/entities?site_id=${siteId}&entity_type=${cfg.noun}&owner_flag=0`);
        let mapped = (Array.isArray(rows) ? rows : []).map(r => entityFromRow(r, cfg));
        if (cfg.noun === "product") {
          const kids = {};
          mapped.forEach(e => { const pid = e._data && e._data.parent_id; if (pid != null) (kids[pid] = kids[pid] || []).push(e); });
          mapped.forEach(e => { if (kids[e.id]) e.variants = kids[e.id].map(k => k.name); });
          mapped = mapped.filter(e => !(e._data && e._data.parent_id != null && mapped.some(p => p.id === e._data.parent_id)));
        }
        setData(mapped);
        setPhase("default");
      } catch { setPhase("error"); }
    }, [wired, siteId, cfg]);
    useEffect(() => { reload(); }, [reload]);
    useEffect(() => {
      if (!wired || siteId == null) { setMarket(null); return; }
      let cancel = false;
      api(`/bi/entities?site_id=${siteId}&owner_flag=1`).then(rows => {
        if (cancel) return;
        const owner = Array.isArray(rows) && rows[0];
        setMarket((owner && owner.data && owner.data.market) || null);
      }).catch(() => {});
      return () => { cancel = true; };
    }, [wired, siteId]);

    const patch = (id, body) => api(`/bi/entities/${id}`, { method: "PATCH", body: JSON.stringify({ site_id: siteId, ...body }) });
    const onOurs = async (id) => {
      if (!wired) { setData(d => d.map(e => e.id === id ? { ...e, ours: !e.ours } : e)); return; }
      const e = data.find(x => x.id === id); if (!e) return;
      setData(d => d.map(x => x.id === id ? { ...x, ours: !x.ours } : x));   // optimistic
      try { await patch(id, { is_ours: e.ours ? 0 : 1 }); } catch { reload(); }
    };
    const onStatus = async (id, status) => { if (!wired) return; try { await patch(id, { validation_status: status }); await reload(); } catch { /* */ } };
    const onMove = async (id, entity_type) => { if (!wired) return; try { await patch(id, { entity_type }); setDetail(null); await reload(); } catch { /* */ } };
    // Delete always asks first (per docs/CHANGELOG: app-styled confirm modal).
    const onDelete = (id) => setConfirmDel({ ids: [id], one: true });
    const doDelete = async (ids) => {
      if (!wired) { setConfirmDel(null); return; }
      for (const id of ids) { try { await api(`/bi/entities/${id}?site_id=${siteId}`, { method: "DELETE" }); } catch { /* */ } }
      setConfirmDel(null); setDetail(null); setSel([]); setSelectMode(false); await reload();
    };
    const onFacet = async (id, field, value) => {
      if (!wired) return;
      const e = data.find(x => x.id === id); if (!e) return;
      const dataKey = FACET_KEY[field] || field;                    // prototype field → backend data key
      const newData = { ...(e._data || {}), [dataKey]: value };
      setData(d => d.map(x => x.id === id ? { ...x, [field]: value, _data: newData } : x));  // optimistic
      try { await patch(id, { data: newData }); } catch { reload(); }
    };
    const bulkStatus = async (status) => { if (!wired || !sel.length) return; for (const id of sel) await patch(id, { validation_status: status }); setSel([]); setSelectMode(false); await reload(); };
    const bulkDelete = () => { if (!sel.length) return; setConfirmDel({ ids: sel.slice() }); };
    const onEdit = (e) => { setEditing(e); setModal("add"); };
    const onShare = (e) => { setShareEntity(e); setModal("share"); };
    const closeModal = () => { setModal(null); setEditing(null); setShareEntity(null); };

    const showState = wired ? (phase === "default" && data.length === 0 ? "empty" : phase) : (state || "default");
    const marketLabel = wired ? market : (marketSet ? "France" : null);

    const lcFn = LIFECYCLE.find(l => l[0] === lifecycle)[3];
    const rows = data.filter(lcFn).filter(e => cfg.filters.every(f => { const v = filters[f.field]; return !v || v === "all" || fmatch(f, e, v); }));
    const archivedView = lifecycle === "archived";

    const detailEntity = detail && data.find(x => x.id === detail.id);
    // A link to one entity arrives before the list it belongs to, so for a
    // moment the id names nothing: the list's own skeletons below are that
    // wait. Once the read has landed, an id that is STILL not here (deleted, or
    // from another site's register) has to say so, instead of dropping the user
    // on a list that never explains why it is not the detail they asked for.
    const detailGone = !!detail && !detailEntity && showState !== "loading";

    /* Whether a detail is open is state this module keeps to itself, which is
       right until something is composed AROUND it. Products renders a "Gaps and
       ideas" panel below the register, and with no way to see this it stayed on
       screen underneath a single product's detail view, where it is about the
       wrong thing entirely.
       Optional, so every other caller is unaffected, and reported through an
       effect rather than during render because a parent cannot be updated while
       a child is rendering. */
    useEffect(() => { if (onViewChange) onViewChange(detailEntity ? 'detail' : 'list'); }, [!!detailEntity, onViewChange]);
    const DetailComp = cfg.DetailComponent || DetailView;
    const CardComp = cfg.CardComponent || EntityCard;
    const RowComp = cfg.RowComponent || EntityRow;
    // Modals must render in BOTH the detail and list views (the detail early-returns),
    // otherwise Edit/Delete from a detail page open nothing.
    const modals = (
      <>
        {modal === "add" && M.AddEditModal && <M.AddEditModal cfg={cfg} edit={editing} site={site} onSaved={reload} onClose={closeModal} />}
        {modal === "grow" && M.GrowModal && <M.GrowModal cfg={cfg} site={site} onSaved={reload} onClose={closeModal} />}
        {modal === "share" && M.ShareModal && <M.ShareModal entity={shareEntity} site={site} sites={sites} onSaved={() => {}} onClose={closeModal} />}
        {confirmDel && M.ConfirmDialog && <M.ConfirmDialog danger
          title={confirmDel.one ? `Delete this ${cfg.noun}?` : `Delete ${confirmDel.ids.length} ${cfg.title.toLowerCase()}?`}
          body="This permanently removes it from your Business Intelligence and can't be undone."
          confirmLabel="Delete" onClose={() => setConfirmDel(null)} onConfirm={() => doDelete(confirmDel.ids)} />}
        {modal === "offerings" && M.AddOfferingsModal && <M.AddOfferingsModal site={site} onSaved={reload} onClose={closeModal} />}
      </>
    );
    if (detailEntity) {
      return <div className="bi-page"><DetailComp cfg={cfg} e={detailEntity} site={site} onSaved={reload} onBack={() => setDetail(null)} onOurs={onOurs} onStatus={onStatus} onDelete={onDelete} onEdit={onEdit} onFacet={onFacet} />{modals}</div>;
    }

    return (
      <div className="bi-page">
        {/* header */}
        <div className="bi-header">
          <div className="bi-header__titles">
            <h1 className="bi-title"><span className="bi-title__icon"><I name={cfg.icon} size={20} /></span>{cfg.title}</h1>
            <div className="bi-subtitle">Business Intelligence · tracked {cfg.title.toLowerCase()} relative to your business</div>
            {marketLabel
              ? <button className="bi-market-chip"><I name="globe" size={15} />{marketLabel} <span className="bi-market-chip__hint">· suggestions constrained to this market</span></button>
              : <button className="bi-market-chip bi-market-chip--unset"><I name="alert-triangle" size={15} />No market set · set one in Business Profile</button>}
          </div>
          <div className="bi-header-actions">
            <div className="bi-viewtoggle" role="tablist" aria-label="View mode">
              <button className={viewMode === "grid" ? "on" : ""} title="Grid view" aria-pressed={viewMode === "grid"} onClick={() => setViewMode("grid")}><I name="grid" size={16} /></button>
              <button className={viewMode === "list" ? "on" : ""} title="List view" aria-pressed={viewMode === "list"} onClick={() => setViewMode("list")}><I name="list" size={16} /></button>
            </div>
            <button className="bi-btn" onClick={() => setModal("grow")}><I name="download" size={16} />Grow from…</button>
            {showState === "default" && <button className="bi-btn" onClick={reload}><I name="refresh" size={16} />Refresh</button>}
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn bi-btn--primary" onClick={() => setModal("add")}><I name="plus" size={16} />Add {cfg.noun}</button>
            {cfg.addOfferings && <button className="bi-btn" onClick={() => setModal("offerings")}><I name="layers" size={16} />Add offerings</button>}
          </div>
        </div>

        {detailGone && <div className="bi-subempty">That link points to an entry that is not in this register. It may have been deleted, or it belongs to another site.</div>}

        {showState === "empty" ? (
          <div className="bi-empty-state">
            <span className="bi-empty-state__icon"><I name={cfg.icon} size={30} /></span>
            <div className="bi-empty-state__title">No {cfg.title.toLowerCase()} tracked yet</div>
            <p className="bi-empty-state__desc">Build your {cfg.title.toLowerCase()} graph by growing from your Business Profile or the live web, then validate each one before it sticks.</p>
            <div className="bi-empty-state__actions">
              <button className="bi-btn bi-btn--primary" onClick={() => setModal("grow")}><I name="download" size={16} />Grow from…</button>
              <button className="bi-btn" onClick={() => setModal("add")}><I name="plus" size={16} />Add a {cfg.noun}</button>
              <button className="bi-btn" onClick={() => go && go("business-profile")}><I name="home" size={16} />Business Profile</button>
            </div>
            <div className="bi-empty-state__hint">The flow: set your <b>Business Profile</b> &amp; market → <b>Grow</b> proposes entities with AI → you <b>validate</b> each before it persists. Nothing is added automatically.</div>
          </div>
        ) : showState === "loading" ? (
          <>
            <StatCards cfg={cfg} active={lifecycle} onPick={setLifecycle} data={data} wired={wired} />
            <div className="bi-list">{[0, 1, 2, 3].map(i => <div className="bi-skeleton" key={i}><div className="bi-skel-line" style={{ width: "45%", height: 16 }} /><div className="bi-skel-line" style={{ width: "70%" }} /><div className="bi-skel-line" style={{ width: "60%" }} /><div className="bi-skel-line" style={{ width: "30%" }} /></div>)}</div>
          </>
        ) : showState === "error" ? (
          <>
            <StatCards cfg={cfg} active={lifecycle} onPick={setLifecycle} data={data} wired={wired} />
            <div className="bi-empty-state" style={{ borderStyle: "solid", borderColor: "var(--danger-border)" }}>
              <span className="bi-empty-state__icon" style={{ background: "var(--danger-surface)", color: "var(--danger)" }}><I name="alert-circle" size={30} /></span>
              <div className="bi-empty-state__title">Couldn't load {cfg.title.toLowerCase()}</div>
              <p className="bi-empty-state__desc">Check your connection and try again. Your data is safe.</p>
              <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={reload}><I name="refresh" size={16} />Retry</button></div>
            </div>
          </>
        ) : (
          <>
            <StatCards cfg={cfg} active={lifecycle} onPick={setLifecycle} data={data} wired={wired} />
            <FilterBar cfg={cfg} active={filters} setActive={setFilter} data={data} />

            {selectMode && (
              <div className="bi-bulk-bar">
                <span className="bi-bulk-bar__count">{sel.length} selected</span>
                <button className="bi-btn bi-btn--sm" onClick={() => setSel(rows.map(e => e.id))}><I name="check-square" size={14} />Select all ({rows.length})</button>
                <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
                <span className="bi-bulk-bar__sep" />
                {archivedView
                  ? <button className="bi-btn bi-btn--sm" disabled={!sel.length} onClick={() => bulkStatus("pending")}><I name="rotate-ccw" size={14} />Restore</button>
                  : <button className="bi-btn bi-btn--sm" disabled={!sel.length} onClick={() => bulkStatus("rejected")}><I name="archive" size={14} />Archive</button>}
                <button className="bi-btn bi-btn--sm" disabled={!sel.length} onClick={() => bulkStatus("validated")}><I name="check-circle" size={14} />Validated</button>
                <button className="bi-btn bi-btn--sm" disabled={!sel.length} onClick={() => bulkStatus("pending")}><I name="clock" size={14} />Pending</button>
                <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={!sel.length} onClick={bulkDelete}><I name="trash" size={14} />Delete</button>
              </div>
            )}

            {rows.length === 0
              ? <div className="bi-subempty">None in this view. Try a different filter or <a className="bi-viewdetails" style={{ display: "inline-flex" }} href="#" onClick={e => { e.preventDefault(); setFilters({}); setLifecycle("tracked"); }}>clear filters</a>.</div>
              : viewMode === "list"
                ? <div className="bi-list--rows">{rows.map(e => <RowComp key={e.id} cfg={cfg} e={e} selectMode={selectMode} selected={sel.includes(e.id)} onToggle={toggle} onOurs={onOurs} onOpen={setDetail} onFacet={onFacet} onShare={onShare} onMove={onMove} />)}</div>
                : <div className="bi-list">{rows.map(e => <CardComp key={e.id} cfg={cfg} e={e} selectMode={selectMode} selected={sel.includes(e.id)} onToggle={toggle} onOurs={onOurs} onOpen={setDetail} onFacet={onFacet} onShare={onShare} onMove={onMove} />)}</div>}
          </>
        )}
        {modals}
      </div>
    );
  }

  window.MlBiHelpers = { valBadge, srcBadge, relBadge, FacetDropdown };
  window.MlBiMaster = { BiModule, COMPETITORS, INDUSTRY, PARTNERS };
})();
